import React, { useContext, useState, useEffect } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { RefreshRightIcon, RotationLeftIcon, DownloadIcon } from '@vlabs/icons';
import { selectMenuPortalProps } from '@vlabs/monokit/shared/ui/select-portal';
import { START_OF_DAY, END_OF_DAY } from '@vlabs/shared/config';
import { downloadJson } from '@vlabs/shared/utils';
import { Panel, Control, SettingsItemWrapper } from '@vlabs/uikit';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Chart } from './chart/Chart';
import { defaultFormValues, INTERVAL_OPTIONS } from './defaultFormValues';
import { downloadCSV } from './downloadCSV';
import { GroupedCheckbox } from './GroupedCheckbox/GroupedCheckbox';
import st from './StatisticTab.module.sass';
import { QueueContext } from '../../queue-context/QueueContext';

function StatisticTab() {
  const [chartData, setChartData] = useState(null);
  const [selectedStreams, setSelectedStreams] = useState([]);

  const { groups, streamIdsForStatistic } = useContext(QueueContext);

  useEffect(() => {
    setSelectedStreams(streamIdsForStatistic);
  }, [streamIdsForStatistic]);

  const {
    control, handleSubmit, formState: { errors }, reset,
  } = useForm({ values: defaultFormValues, mode: 'onChange', shouldFocusError: false });
  const { t } = useTranslation();

  const fetchChartData = async (formValues) => {
    setChartData(null);

    if (selectedStreams.length < 1) {
      toast.error(t('queue:statistic tab.validation error.stream required'));
      return;
    }

    // получаем данные статистики по каждому выбранному стриму, подклеиваем к нему имя и threshold
    const allStatistics = selectedStreams.map(async (id) => {
      // получаем данные о стриме и вытаскиваем из них имя для легенды на графике
      // и пороговое значение для отрисовки линии
      const { name, description } = await apiContainer.lunaClient.streams.get(id);
      const { zone_count_threshold } = JSON.parse(description);

      try {
        return apiContainer.lunaClient.generalEvents.getStatistic({
          targets: formValues.targets,
          group_by: formValues.group_by.value,
          period: formValues.period,
          filters: { ...formValues.filters[0], value: id },
        }).then((response) => {
          return {
            streamName: name,
            threshold: zone_count_threshold,
            data: response.data,
          };
        });
      } catch (error) {
        console.error(error);
        toast.error(t('queue:statistic tab.query error.request statistic error', { streamId: id }));
        return null;
      }
    });

    Promise.all(allStatistics).then((response) => {
      const data = response.filter(Boolean).map((r) => ({
        streamName: r?.streamName,
        threshold: r?.threshold,
        ...r?.data,
      }));

      setChartData(data);
    });
  };

  const handleReset = () => {
    reset(defaultFormValues);
    setSelectedStreams(streamIdsForStatistic);
    setChartData(null);
  };

  return (
    <div className={st.Root}>
      <Panel className={classNames(st.Settings, 'PanelAutoHeight')}>
        <div className={st.ScrollableContent}>
          <form
            id="statisticForm"
            onSubmit={handleSubmit(fetchChartData)}
          >
            <div className={st.Title}>{t('queue:statistic tab.form.zones.title')}</div>
            {groups.map((g) => (
              <GroupedCheckbox
                groupName={g}
                key={g}
                selectedStreams={selectedStreams}
                setSelectedStreams={setSelectedStreams}
              />
            ))}
            <div className={st.Divider} />
            <div className={st.Title}>{t('queue:statistic tab.form.other settings.title')}</div>
            <div className={st.ColorPanel}>
              <SettingsItemWrapper
                contentClassName={st.SettingsItem__Content}
                title={t('queue:statistic tab.form.other settings.period from.title')}
                titleClassName={st.SettingsItem__Title}
              >
                <Control.DateInput
                  control={control}
                  enableTime
                  errors={errors}
                  id="period.gt"
                  name="period.gt"
                  options={START_OF_DAY}
                />
              </SettingsItemWrapper>

              <SettingsItemWrapper
                contentClassName={st.SettingsItem__Content}
                title={t('queue:statistic tab.form.other settings.period to.title')}
                titleClassName={st.SettingsItem__Title}
              >
                <Control.DateInput
                  control={control}
                  enableTime
                  errors={errors}
                  id="period.lt"
                  name="period.lt"
                  options={END_OF_DAY}
                />
              </SettingsItemWrapper>

              <SettingsItemWrapper
                contentClassName={st.SettingsItem__Content}
                title={t('queue:statistic tab.form.other settings.interval.title')}
                titleClassName={st.SettingsItem__Title}
              >
                <Control.Select
                  control={control}
                  errors={errors}
                  id="group_by"
                  name="group_by"
                  options={INTERVAL_OPTIONS}
                  {...selectMenuPortalProps}
                />
              </SettingsItemWrapper>
            </div>
          </form>
        </div>
        <div className={st.BottomButtonGroup}>
          <Control.Button
            data-testid="submitButton"
            form="statisticForm"
            icon={<RefreshRightIcon />}
            type="submit"
          >
            {t('queue:statistic tab.form.buttons.submit button.label')}
          </Control.Button>
          <Control.Button
            data-testid="resetButton"
            icon={<RotationLeftIcon />}
            kind="negative"
            onClick={handleReset}
          >
            {t('queue:statistic tab.form.buttons.reset button.label')}
          </Control.Button>
        </div>
      </Panel>
      <Panel className={classNames(st.Graphic, 'PanelAutoHeight')}>
        <div className={st.ChartPanelButtonGroup}>
          <Control.Button
            data-testid="downloadJsonButton"
            disabled={!chartData || chartData.length < 1}
            icon={<DownloadIcon />}
            kind="secondary"
            onClick={() => downloadJson('statistic', chartData)}
          >
            JSON
          </Control.Button>

          <Control.Button
            data-testid="downloadCsvButton"
            disabled={!chartData || chartData.length < 1}
            icon={<DownloadIcon />}
            kind="secondary"
            onClick={() => downloadCSV('statistic', chartData)}
          >
            CSV
          </Control.Button>
        </div>
        <Chart chartData={chartData} />
      </Panel>
    </div>
  );
}

const memoizedStatisticTab = React.memo(StatisticTab);

export { memoizedStatisticTab as StatisticTab };
